import {Tag, Deal, Team, RenewalContact, AutomationType} from "@irati/types"
import service from "@irati-tracking/tracker"

export interface Reporting {
  source?: "Navigation" | string
  type?: string
  viewType?: ViewType
  filter?: Filter
  product?: Product
  action?: string
  metric?: string
}

export enum ViewType {
  default = "Default",
  filtered = "Filtered"
}

export enum Filter {
  country = "Country",
  team = "Team",
  date = "Date",
  currency = "Currency",
  industry = "Industry",
  zone = "Zone",
  zones = "Zones"
}

export enum Product {
  perks = "Perks",
  flexipoints = "Flexipoints",
  celebration = "Celebration",
  wellness = "Wellness",
  recognitionReceived = "recognitionReceived",
  recognitionGiven = "recognitionGiven",
  pointsBought = "pointsBought",
  pointsTransferred = "pointsTransferred",
  pointsSpent = "pointsSpent",
  managerWallet = "managerWallet"
}

export interface RenewalPlan {
  source?: RenewalPlanSource
  type?: RenewalPlanType
  subSource?: RenewalPlanSubSource
  page?: string
  stage?: number
  plan?: string
  reason?: string
  action?: string
  editParamater?: string
  contactDetail?: RenewalContact
  changeSeats?: number
  changeFP?: string
  changeLocal?: number
  EAPUpgrade?: string
  product?: string
}

export interface HRISUpsell {
  source?: "Navigation" | string
  viewType?: "Default" | string
  action?: string
  date?: string
}

export enum RenewalPlanType {
  renewalFlow = "renewal_Flow",
  cancellationFlow = "cancellation_Flow",
  changePlanFlow = "changePlan_Flow"
}

export enum RenewalPlanSubSource {
  renewPlanBannerCTA = "renewPlan_BannerCTA",
  currentPlanCTA = "currentPlan_CTA",
  subnav = "subnav",
  confirmDetails = "confirmDetails",
  winbackCTA = "winback_CTA",
  cancelCTA = "cancel_CTA",
  nothanksCTA = "nothanks_CTA",
  offerproceedCTA = "offerproceed_CTA",
  submitRenewal = "submitRenewal"
}

export enum RenewalPlanPage {
  currentplanStage = "currentplan_Stage",
  newplanStage = "newplan_Stage",
  confirmplanStage = "confirmplan_Stage",
  renewalstatusStage = "renewalstatus_Stage",
  changeplanStage = "changeplan_Stage",
  reasonStage = "reason_Stage",
  offerStage = "offer_Stage",
  cancelconfirmStage = "cancelconfirm_Stage"
}

export enum RenewalPlanPlan {
  TwelvemonthRenewal = "12month-renewal",
  TwentyFourMonthRenewal = "24month-renewal",
  ThirtySixMonthRenewal = "36month-renewal",
  Winback = "12month-winback"
}

export enum RenewalPlanSource {
  email = "email",
  subnav = "subnav",
  reviewRenewalPlan = "review_renewalplan",
  reviewRenewalStatus = "review_renewalStatus",
  perkboxStory = "perkbox_story",
  renewalBanner = "renewal_banner"
}

export enum HubViews {
  AUTOMATION = "automation",
  CUSTOM_REWARDS = "custom_rewards"
}

export enum CustomRewardTypes {
  ADD_CUSTOM_REWARD = "add_custom_reward",
  CREATE_CUSTOM_REWARD = "create_custom_reward",
  MARK_CUSTOM_REWARD = "mark_custom_reward",
  VIEW_TRANSACTIONS = "view_custom_rewards_transactions"
}

const tracking = {
  changeTagVisibility: (tag: Tag, newValue: string) => {
    const data = {
      tag_id: tag.uuid,
      tag_new_value: newValue,
      tag_name: tag.name
    }
    service.trackEvent("admin_change_perks_tag_visibility", data)
  },

  changePerkVisibility: (perk: Deal, newValue: string) => {
    const data = {
      perk_id: perk.uuid,
      perk_new_value: newValue,
      perk_name: perk.header
    }
    service.trackEvent("admin_change_perk_visibility", data)
  },

  createTeam: (teamName: string) => {
    const data = {team_name: teamName}
    service.trackEvent("admin_create_team", data)
  },

  startCreateUser: (createType: string) => {
    switch (createType) {
      case "add_people-single":
        service.trackEvent("admin_start_create_user")
        break
      case "add_people-multiple":
        service.trackEvent("admin_start_create_users")
        break
      default:
        return null
    }
  },

  createUser: (access: string, teams: [Team]) => {
    const data = {
      access_level: access,
      team: teams
    }
    service.trackEvent("admin_create_user", data)
  },

  createUsers: () => {
    service.trackEvent("admin_create_users")
  },

  startEnableSSO: () => {
    service.trackEvent("admin_start_enable_sso")
  },

  enableSSO: () => {
    service.trackEvent("admin_enable_sso")
  },

  enableSSU: () => {
    service.trackEvent("admin_enable_ssu")
  },

  updateWelcomeEmail: () => {
    service.trackEvent("admin_update_welcome_email")
  },

  updateLogo: () => {
    service.trackEvent("admin_update_logo")
  },

  updateColorScheme: () => {
    service.trackEvent("admin_update_colour_scheme")
  },

  downloadRewardLog: () => {
    service.trackEvent("admin_download_rewards")
  },

  editUserExternalID: (userId: string) => {
    const data = {
      userId
    }
    service.trackEvent("admin_edit_external_id", data)
  },

  viewHelp: () => {
    service.trackEvent("admin_view_help")
  },

  viewTrainingWebinars: () => {
    service.trackEvent("admin_view_training_webinars")
  },

  viewLaunchingPerkbox: () => {
    service.trackEvent("admin_view_launching_perkbox")
  },

  viewCustomisation: () => {
    service.trackEvent("admin_view_customisation")
  },

  viewPeopleManagement: () => {
    service.trackEvent("admin_view_people_management")
  },

  viewBillingPayments: () => {
    service.trackEvent("admin_view_billing_payments")
  },

  addRegions: () => {
    service.trackEvent("admin_regions_addRegions")
  },

  viewRegions: () => {
    service.trackEvent("admin_regions_view")
  },

  assignTeams: () => {
    service.trackEvent("admin_people_multiselect_assignTeams")
  },

  assignTeamsConfirmed: () => {
    service.trackEvent("admin_people_multiselect_assignTeams_confirmed")
  },

  assignTeamsSetPrimaryTeam: () => {
    service.trackEvent("admin_people_multiselect_assignTeams_setPrimaryteam")
  },

  assignTeamsSetOtherTeam: () => {
    service.trackEvent("admin_people_multiselect_assignTeams_setOtherteam")
  },

  deativate: () => {
    service.trackEvent("admin_people_multiselect_deactivate")
  },

  deactivateConfirmed: () => {
    service.trackEvent("admin_people_multiselect_deactivate_confirmed")
  },

  searchPeople: () => {
    service.trackEvent("admin_people_searchPeople")
  },

  filterRegion: () => {
    service.trackEvent("admin_people_filterRegion")
  },

  filterStatusRole: () => {
    service.trackEvent("admin_people_filterStatusRole")
  },

  filterSortBy: () => {
    service.trackEvent("admin_people_sortBy")
  },

  viewUser: () => {
    service.trackEvent("admin_people_viewsUser")
  },

  editsRegion: () => {
    service.trackEvent("admin_people_viewsUser_editsRegion")
  },

  editsRole: () => {
    service.trackEvent("admin_people_viewsUser_editsRole")
  },

  editsTeam: () => {
    service.trackEvent("admin_people_viewsUser_editsTeam")
  },

  deactivatesUser: () => {
    service.trackEvent("admin_people_viewsUser_deactivates")
  },

  resetPassword: () => {
    service.trackEvent("admin_people_viewsUser_resetPassword")
  },

  reactivatesUser: () => {
    service.trackEvent("admin_people_viewsUser_reactivates")
  },

  companyBenefitsAdd: () => {
    service.trackEvent("admin_settings_addCompanyBenefits")
  },

  companyBenefitsSelectAudience: () => {
    service.trackEvent("admin_settings_addCompanyBenefits_selectAudience")
  },

  companyBenefitsCreateTitle: () => {
    service.trackEvent("admin_settings_addCompanyBenefits_createsTitle")
  },

  companyBenefitsAddDescription: () => {
    service.trackEvent("admin_settings_addCompanyBenefits_addsDescription")
  },

  companyBenefitsWritesInstructions: () => {
    service.trackEvent("admin_settings_addCompanyBenefits_writesInstructions")
  },

  setPrimaryTeam: () => {
    service.trackEvent("admin_people_viewsUser_setPrimaryTeam")
  },

  userExport: () => {
    service.trackEvent("admin_user_export")
  },

  analyticsContent: () => {
    service.trackEvent("admin_analytics_content")
  },

  analyticsClickBreadownEditTeams: () => {
    service.trackEvent("admin_analytics_breadownEditTeams")
  },

  analyticsSetDefaultBenchmark: () => {
    service.trackEvent("admin_analytics_overall_setDefaultBenchmark")
  },

  bulkAssignManagers: () => {
    service.trackEvent("admin_teams_bulkAssignManagers_start")
  },
  bulkAssignManagersSuccessful: () => {
    service.trackEvent("admin_teams_bulkAssignManagers_successful")
  },
  filterMostPopularPerks: () => {
    service.trackEvent("admin_interests_filterMostPopularPerks")
  },
  globalModalAddCountries: () => {
    service.trackEvent("admin_globalModal_addCountries")
  },
  globalModalWhatIsThis: () => {
    service.trackEvent("admin_globalModal_whatIsThis")
  },
  globalModalWhatIsThisNotRelevant: () => {
    service.trackEvent("admin_globalModal_whatisThis_notRelevant")
  },
  globalModalWhatIsThisAddCountries: () => {
    service.trackEvent("admin_globalModal_whatisThis_addCountries")
  },
  globalModalClose: () => {
    service.trackEvent("admin_globalModal_close")
  },
  globalModalNotRelevantConfirm: () => {
    service.trackEvent("admin_globalModal_whatisThis_notRelevant_confirm")
  },
  navigationClickHome: () => {
    service.trackEvent("admin_navTabv2_home")
  },
  navigationClickPeople: () => {
    service.trackEvent("admin_navTabv2_people")
  },
  navigationClickTeams: () => {
    service.trackEvent("admin_navTabv2_teams")
  },
  navigationClickCountries: () => {
    service.trackEvent("admin_navTabv2_countries")
  },
  navigationClickReporting: () => {
    service.trackEvent("admin_navTabv2_reporting")
  },
  navigationClickAutomation: () => {
    service.trackEvent("admin_navTabv2_automation")
  },
  navigationHubView: (source: "navigation" | "deeplink", type: HubViews) => {
    service.trackEvent("admin_hub_view", {
      source,
      type
    })
  },
  navigationClickReportingCompany: () => {
    service.trackEvent("admin_navTabv2_reporting_company")
  },
  navigationClickReportingGroups: () => {
    service.trackEvent("admin_navTabv2_reporting_groups")
  },
  navigationClickReportingIndividuals: () => {
    service.trackEvent("admin_navTabv2_reporting_individuals")
  },
  navigationClickReportingInterests: () => {
    service.trackEvent("admin_navTabv2_reporting_interests")
  },
  navigationClickCustomisations: () => {
    service.trackEvent("admin_navTabv2_customisations")
  },
  navigationClickCustomisationsLogo: () => {
    service.trackEvent("admin_navTabv2_customisations­_logo")
  },
  navigationClickCustomisationsPerkVisibility: () => {
    service.trackEvent("admin_navTabv2_customisations­_perkVisibility")
  },
  navigationClickCustomisationsCustomBenefits: () => {
    service.trackEvent("admin_navTabv2_customisations­_customBenefits")
  },
  navigationClickCustomisationsCompanyValues: () => {
    service.trackEvent("admin_navTabv2_customisations­_companyValues")
  },
  navigationClickCustomisationsCompanyCelebrations: () => {
    service.trackEvent("admin_navTabv2_customisations­_companyCelebrations")
  },
  navigationClickCustomisationsCustomReward: () => {
    service.trackEvent("admin_navTabv2_customisations­_customReward")
  },
  navigationClickIntegrations: () => {
    service.trackEvent("admin_navTabv2_integrations")
  },
  navigationClickBilling: () => {
    service.trackEvent("admin_navTabv2_billing")
  },
  navigationClickSettings: () => {
    service.trackEvent("admin_navTabv2_settings")
  },
  navigationClickSettingsSelfSignup: () => {
    service.trackEvent("admin_navTabv2_settings_selfSignup")
  },
  navigationClickSettingsRolePermissions: () => {
    service.trackEvent("admin_navTabv2_settings_rolePermissions")
  },
  navigationClickHelp: () => {
    service.trackEvent("admin_navTabv2_help")
  },
  navigationClickRewards: () => {
    service.trackEvent("admin_navTabv2_rewardWallet")
  },
  navigationClickRewardsNew: () => {
    service.trackEvent("admin_click_reward_nav")
  },
  navigationExpandCollapseMenu: () => {
    service.trackEvent("admin_navTabv2_menuExpandCollapse")
  },
  submitGlobalModalReasons: (reasons: string[]) => {
    service.trackEvent("admin_globalModal_close_submitReasons", {reasons})
  },
  adminHeaderClickBackwardsForwardsButton: () => {
    service.trackEvent("admin_click_backwardsFowards_buttons")
  },
  adminHeaderClickBreadcrumbs: () => {
    service.trackEvent("admin_click_breadcrumbs")
  },
  adminHeaderClickAdminHub: () => {
    service.trackEvent("admin_click_adminHub")
  },
  adminNavMainMenuExpanseCollapse: () => {
    service.trackEvent("admin_navTabv2_mainMenuExpandCollapse")
  },
  adminNavSubMenuExpanseCollapse: () => {
    service.trackEvent("admin_navTabv2_subMenuExpandCollapse")
  },
  triggerWorkOSIntegrationsSSO: () => {
    service.trackEvent("admin_settings_integrations_sso_shortcutLink_start")
  },
  triggerWorkOSIntegrationsDsync: () => {
    service.trackEvent("admin_settings_integrations_dsync_shortcutLink_start")
  },
  copyLinkWorkOSIntegrationsSSO: () => {
    service.trackEvent("admin_settings_integrations_sso_shortcutLink_linkCopied")
  },
  copyLinkWorkOSIntegrationsDsync: () => {
    service.trackEvent("admin_settings_integrations_dsync_shortcutLink_linkCopied")
  },
  adminPeopleAddCountry: (source: string) => {
    service.trackEvent("admin_people_countryDropdown_selectAddCountry", {source})
  },
  adminPeopleConfirmAddCountry: (source: string) => {
    service.trackEvent("admin_people_countryDropdown_confirmAddCountry", {source})
  },
  unusedLicensesClick: () => {
    service.trackEvent("admin_people_unusedLicenses_clickAddUsers")
  },
  clickDeleteCountryIcon: () => {
    service.trackEvent("admin_countries_deleteCountry_start")
  },
  clickConfirmDelete: () => {
    service.trackEvent("admin_countries_deleteCountry_confirm")
  },
  submitDeleteCountryReasons: (reasons: string[]) => {
    service.trackEvent("admin_countries_deleteCountry", {reasons})
  },
  startIntegrationsSSOJourney: (source: string, reason?: string) => {
    service.trackEvent("admin_integrations_ssoJourneyStart", {source, reason})
  },
  startIntegrationsDSYNCJourney: (source: string) => {
    service.trackEvent("admin_integrations_dsyncJourneyStart", {source})
  },
  editIntegrationSSOJourney: (source: string) => {
    service.trackEvent("admin_integrations_ssoEdit", {source})
  },
  adminDashboardOnboardingV2Action: (source: string, action: string) => {
    service.trackEvent("admin_dashboard_onboardingV2", {source, action})
  },
  reporting: (data: Reporting) => {
    service.trackEvent("admin_reporting", data)
  },
  submitOnboardingV2Feedback: (source: string, reason: string) => {
    service.trackEvent("admin_dashboard_onboardingV2_reasons", {source, action: "feedback", reason})
  },
  renewalPlan: (data: RenewalPlan) => {
    service.trackEvent("adminbilling_Plan", data)
  },
  navigationClickDsyncIntegrationPage: () => {
    service.trackEvent("admin_people_cross_sell_DYSNC")
  },
  navigationClickAddSinglePerson: () => {
    service.trackEvent("admin_people_add_single_person")
  },
  navigationClickAddByCSV: () => {
    service.trackEvent("admin_people_csv_upload")
  },
  navigationClickDeactivateByCSV: () => {
    service.trackEvent("admin_people_deactive_csv")
  },
  donloadPeopleListFromPeoplePage: () => {
    service.trackEvent("admin_people_download_people")
  },
  dsyncSetup: (action: string) => {
    service.trackEvent("admin_dysnc_setup", {action})
  },
  ssoSetup: (action: string) => {
    service.trackEvent("admin_integrations_sso", {action})
  },
  hrisUpsellSetup: (event: string, data: HRISUpsell) => {
    service.trackEvent(event, {...data})
  },
  automationClick: (type: string) => {
    service.trackEvent("admin_hub_click", {source: HubViews.AUTOMATION, type})
  },
  recognitionAutomationConfirmation: (type: "activate" | "deactivate" | "save", automationType: AutomationType) => {
    service.trackEvent("automation", {type, automationType})
  },
  yourPerkboxStory: (data: RenewalPlan) => {
    service.trackEvent("adminbilling_Story", data)
  },
  ctaAdminActivationInvite: () => {
    service.trackEvent("cta_admin_activation_invite")
  },
  sendCustomRewardEvent: (type: CustomRewardTypes) => {
    service.trackEvent("admin_hub_click", {source: HubViews.CUSTOM_REWARDS, type, product: HubViews.CUSTOM_REWARDS})
  }
}

export default tracking
